import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Car, MapPin, Calendar, Clock, Users, FileText, Send, User, Phone, Mail, MapPinned, Navigation, Loader } from 'lucide-react';
import api from '../../config/api';

const BookVehicle = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contactNumber: '',
    pickupLocation: '',
    pickupState: '',
    pickupCity: '',
    pickupCoordinates: {
      latitude: null,
      longitude: null
    },
    dropLocation: '',
    dropState: '',
    dropCity: '',
    date: '',
    time: '',
    passengers: '',
    purpose: '',
    additionalInfo: ''
  });
  
  // Fetch states on component mount
  useEffect(() => {
    const fetchStates = async () => {
      setLoadingStates(true);
      try {
        // Use the correct API endpoint
        const response = await api.get('/location/states');
        if (response.data.success) {
          const stateList = Object.keys(response.data.data).sort();
          setStates(stateList);
        }
      } catch (error) {
        console.error('Error fetching states:', error);
        toast.error('Failed to load states');
      } finally {
        setLoadingStates(false);
      }
    };

    fetchStates();
  }, []);

  // State to store cities for both pickup and drop locations
  const [pickupCities, setPickupCities] = useState([]);
  const [dropCities, setDropCities] = useState([]);

  // Fetch cities when pickup state changes
  useEffect(() => {
    if (formData.pickupState) {
      const fetchCities = async () => {
        setLoadingCities(true);
        try {
          // Use the correct API endpoint
          const response = await api.get(`/location/cities/${formData.pickupState}`);
          if (response.data.success) {
            const sortedCities = response.data.data.sort();
            setPickupCities(sortedCities);
            setCities(sortedCities); // Keep for backward compatibility
          }
        } catch (error) {
          console.error('Error fetching pickup cities:', error);
          toast.error('Failed to load pickup cities');
        } finally {
          setLoadingCities(false);
        }
      };

      fetchCities();
    } else {
      setPickupCities([]);
      setCities([]);
    }
  }, [formData.pickupState]);

  // Fetch cities when drop state changes
  useEffect(() => {
    if (formData.dropState) {
      // If drop state is same as pickup state, use the same cities
      if (formData.dropState === formData.pickupState) {
        setDropCities(pickupCities);
        return;
      }
      
      const fetchDropCities = async () => {
        try {
          // Use the correct API endpoint
          const response = await api.get(`/location/cities/${formData.dropState}`);
          if (response.data.success) {
            setDropCities(response.data.data.sort());
          }
        } catch (error) {
          console.error('Error fetching drop cities:', error);
          toast.error('Failed to load drop cities');
        }
      };

      fetchDropCities();
    } else {
      setDropCities([]);
    }
  }, [formData.dropState, formData.pickupState, pickupCities]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'pickupState') {
      setFormData({
        ...formData,
        [name]: value,
        pickupCity: ''
      });
    } else if (name === 'dropState') {
      setFormData({
        ...formData,
        [name]: value,
        dropCity: ''
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Function to get user's current location
  const getCurrentLocation = () => {
    setLoadingLocation(true);
    
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      setLoadingLocation(false);
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          
          console.log('Detected coordinates:', { latitude, longitude });
          
          // Use reverse geocoding to get address from coordinates
          // For this example, we'll use the free Nominatim API (OpenStreetMap)
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
            { headers: { 'Accept-Language': 'en' } }
          );
          
          const data = await response.json();
          
          if (data && data.address) {
            // Extract all possible address components for an Indian address
            const houseNumber = data.address.house_number || '';
            const street = data.address.road || '';
            const neighbourhood = data.address.neighbourhood || '';
            const suburb = data.address.suburb || '';
            const village = data.address.village || '';
            const hamlet = data.address.hamlet || '';
            const district = data.address.district || '';
            const county = data.address.county || '';
            const city = data.address.city || data.address.town || data.address.village || '';
            const state = data.address.state || '';
            const postcode = data.address.postcode || '';
            const country = data.address.country || 'India';
            
            // Get the display name which often has the most complete address
            const displayName = data.display_name || '';
            
            // Create a detailed Indian address format
            let completeAddress = '';
            
            if (houseNumber && street) {
              completeAddress = `${houseNumber}, ${street}`;
            } else if (street) {
              completeAddress = street;
            } else {
              completeAddress = displayName.split(',').slice(0, 3).join(',');
            }
            
            // Add colony/neighborhood information
            if (neighbourhood) {
              completeAddress += `, ${neighbourhood} Colony`;
            } else if (suburb) {
              completeAddress += `, ${suburb} Area`;
            }
            
            // Add landmark if available
            const landmark = data.address.amenity || data.address.shop || '';
            if (landmark) {
              completeAddress += `, Near ${landmark}`;
            }
            
            // Create a complete formatted address
            const formattedAddress = completeAddress || displayName.split(',').slice(0, 4).join(',');
            
            console.log('Detected address details:', data.address);
            
            // Update form data with detected location and coordinates
            const updatedCoordinates = {
              latitude: parseFloat(latitude.toFixed(6)),
              longitude: parseFloat(longitude.toFixed(6))
            };
            
            console.log('Setting coordinates in form:', updatedCoordinates);
            
            setFormData(prevData => ({
              ...prevData,
              pickupLocation: formattedAddress,
              pickupCoordinates: updatedCoordinates
            }));
            
            // If state is available and in our list, select it
            if (state && states.includes(state)) {
              setFormData(prevData => ({
                ...prevData,
                pickupState: state
              }));
              
              // Fetch cities for this state
              try {
                const citiesResponse = await api.get(`/location/cities/${state}`);
                if (citiesResponse.data.success) {
                  const sortedCities = citiesResponse.data.data.sort();
                  setPickupCities(sortedCities);
                  setCities(sortedCities);
                  
                  // If city is available and in our list, select it
                  if (city && sortedCities.includes(city)) {
                    setFormData(prevData => ({
                      ...prevData,
                      pickupCity: city
                    }));
                  }
                }
              } catch (error) {
                console.error('Error fetching cities for detected state:', error);
              }
            }
            
            toast.success('Location detected successfully');
          } else {
            toast.error('Could not determine your address');
          }
        } catch (error) {
          console.error('Error getting location details:', error);
          toast.error('Failed to get location details');
        } finally {
          setLoadingLocation(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        let errorMessage = 'Failed to get your location';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please allow location access.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
          default:
            errorMessage = 'An unknown error occurred.';
        }
        
        toast.error(errorMessage);
        setLoadingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Define required fields
      const requiredFields = [
        { name: 'name', label: 'Full Name' },
        { name: 'email', label: 'Email' },
        { name: 'contactNumber', label: 'Contact Number' },
        { name: 'pickupLocation', label: 'Pickup Location' },
        { name: 'pickupState', label: 'Pickup State' },
        { name: 'pickupCity', label: 'Pickup City' },
        { name: 'dropLocation', label: 'Drop Location' },
        { name: 'dropState', label: 'Drop State' },
        { name: 'dropCity', label: 'Drop City' },
        { name: 'date', label: 'Date' },
        { name: 'time', label: 'Time' },
        { name: 'passengers', label: 'Number of Passengers' },
        { name: 'purpose', label: 'Purpose of Travel' }
      ];
      
      // Check for empty required fields
      for (const field of requiredFields) {
        if (!formData[field.name] || formData[field.name].trim() === '') {
          toast.error(`${field.label} is required`);
          setLoading(false);
          return;
        }
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error('Please enter a valid email address');
        setLoading(false);
        return;
      }

      // Phone validation
      const phoneRegex = /^[0-9]{10}$/;
      if (!phoneRegex.test(formData.contactNumber)) {
        toast.error('Please enter a valid 10-digit contact number');
        setLoading(false);
        return;
      }
      
      // Validate number of passengers is a positive number
      const passengers = parseInt(formData.passengers);
      if (isNaN(passengers) || passengers < 1) {
        toast.error('Number of passengers must be at least 1');
        setLoading(false);
        return;
      }

      // Format pickup and drop locations with state and city
      const formattedPickupLocation = `${formData.pickupLocation}, ${formData.pickupCity}, ${formData.pickupState}`;
      const formattedDropLocation = `${formData.dropLocation}, ${formData.dropCity}, ${formData.dropState}`;
      
      // Ensure coordinates are properly formatted for API submission
      const bookingData = {
        ...formData,
        pickupLocation: formattedPickupLocation,
        dropLocation: formattedDropLocation,
        pickupCoordinates: formData.pickupCoordinates && formData.pickupCoordinates.latitude && formData.pickupCoordinates.longitude
          ? {
              latitude: parseFloat(formData.pickupCoordinates.latitude),
              longitude: parseFloat(formData.pickupCoordinates.longitude)
            }
          : null,
        status: 'pending'
      };
      
      console.log('Sending booking data with coordinates:', bookingData.pickupCoordinates);
      
      // Display coordinates to user for confirmation
      if (bookingData.pickupCoordinates && bookingData.pickupCoordinates.latitude) {
        toast.success(`Location coordinates captured: ${bookingData.pickupCoordinates.latitude.toFixed(6)}, ${bookingData.pickupCoordinates.longitude.toFixed(6)}`);
      }

      // Use the correct API endpoint
      await api.post('/public/vehicle-bookings', bookingData);

      toast.success('Vehicle booking request submitted successfully');
      setFormData({
        name: '',
        email: '',
        contactNumber: '',
        pickupLocation: '',
        pickupState: '',
        pickupCity: '',
        pickupCoordinates: {
          latitude: null,
          longitude: null
        },
        dropLocation: '',
        dropState: '',
        dropCity: '',
        date: '',
        time: '',
        passengers: '',
        purpose: '',
        additionalInfo: ''
      });
    } catch (error) {
      console.error('Error submitting booking request:', error);
      toast.error(error.response?.data?.message || 'Failed to submit booking request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 sm:px-0">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center">
          <Car className="h-8 w-8 text-red-600 mr-3" />
          <h1 className="text-xl font-bold text-gray-900">Book a Vehicle</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h2 className="text-lg font-medium text-gray-900">Vehicle Booking Request</h2>
            <p className="mt-1 text-sm text-gray-500">
              Fill in the details below to request a vehicle for emergency transportation
            </p>
          </div>
          
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
                </div>
                
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Full Name *
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      className="focus:ring-red-500 focus:border-red-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="contactNumber" className="block text-sm font-medium text-gray-700">
                    Contact Number *
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      name="contactNumber"
                      id="contactNumber"
                      className="focus:ring-red-500 focus:border-red-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                      placeholder="10-digit mobile number"
                      value={formData.contactNumber}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="sm:col-span-2">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email Address *
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      className="focus:ring-red-500 focus:border-red-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                      placeholder="Enter your email address"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="sm:col-span-2">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 mt-4">Trip Details</h3>
                </div>
                
                <div className="sm:col-span-2">
                  <label htmlFor="pickupLocation" className="block text-sm font-medium text-gray-700">
                    Pickup Location *
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MapPin className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="pickupLocation"
                      id="pickupLocation"
                      className="focus:ring-red-500 focus:border-red-500 block w-full pl-10 pr-16 sm:text-sm border-gray-300 rounded-md"
                      placeholder="Enter street address"
                      value={formData.pickupLocation}
                      onChange={handleChange}
                      required
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <button
                        type="button"
                        onClick={getCurrentLocation}
                        disabled={loadingLocation}
                        className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        title="Use current location"
                      >
                        {loadingLocation ? (
                          <Loader className="h-4 w-4 animate-spin" />
                        ) : (
                          <Navigation className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="pickupState" className="block text-sm font-medium text-gray-700">
                    Pickup State *
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MapPinned className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      name="pickupState"
                      id="pickupState"
                      className="focus:ring-red-500 focus:border-red-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                      value={formData.pickupState}
                      onChange={handleChange}
                      required
                      disabled={loadingStates}
                    >
                      <option value="">Select State</option>
                      {states.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                    {loadingStates && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-red-500"></div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="pickupCity" className="block text-sm font-medium text-gray-700">
                    Pickup City *
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MapPinned className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      name="pickupCity"
                      id="pickupCity"
                      className="focus:ring-red-500 focus:border-red-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                      value={formData.pickupCity}
                      onChange={handleChange}
                      required
                      disabled={!formData.pickupState || loadingCities}
                    >
                      <option value="">Select City</option>
                      {pickupCities.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </select>
                    {loadingCities && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-red-500"></div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="dropLocation" className="block text-sm font-medium text-gray-700">
                    Drop Location *
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MapPin className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="dropLocation"
                      id="dropLocation"
                      className="focus:ring-red-500 focus:border-red-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                      placeholder="Enter street address"
                      value={formData.dropLocation}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="dropState" className="block text-sm font-medium text-gray-700">
                    Drop State *
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MapPinned className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      name="dropState"
                      id="dropState"
                      className="focus:ring-red-500 focus:border-red-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                      value={formData.dropState}
                      onChange={handleChange}
                      required
                      disabled={loadingStates}
                    >
                      <option value="">Select State</option>
                      {states.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="dropCity" className="block text-sm font-medium text-gray-700">
                    Drop City *
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MapPinned className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      name="dropCity"
                      id="dropCity"
                      className="focus:ring-red-500 focus:border-red-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                      value={formData.dropCity}
                      onChange={handleChange}
                      required
                      disabled={!formData.dropState}
                    >
                      <option value="">Select City</option>
                      {dropCities.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </select>
                    {formData.dropState && dropCities.length === 0 && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-red-500"></div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                    Date *
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Calendar className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="date"
                      name="date"
                      id="date"
                      className="focus:ring-red-500 focus:border-red-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                      value={formData.date}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="time" className="block text-sm font-medium text-gray-700">
                    Time *
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Clock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="time"
                      name="time"
                      id="time"
                      className="focus:ring-red-500 focus:border-red-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                      value={formData.time}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="passengers" className="block text-sm font-medium text-gray-700">
                    Number of Passengers
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Users className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      name="passengers"
                      id="passengers"
                      className="focus:ring-red-500 focus:border-red-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                      placeholder="1"
                      min="1"
                      value={formData.passengers}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="purpose" className="block text-sm font-medium text-gray-700">
                    Purpose of Trip
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FileText className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      name="purpose"
                      id="purpose"
                      className="focus:ring-red-500 focus:border-red-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                      value={formData.purpose}
                      onChange={handleChange}
                    >
                      <option value="">Select purpose</option>
                      <option value="medical">Medical Emergency</option>
                      <option value="evacuation">Evacuation</option>
                      <option value="relief">Relief Work</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700">
                    Additional Information
                  </label>
                  <div className="mt-1">
                    <textarea
                      id="additionalInfo"
                      name="additionalInfo"
                      rows={3}
                      className="shadow-sm focus:ring-red-500 focus:border-red-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      placeholder="Any special requirements or details about the emergency"
                      value={formData.additionalInfo}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/vehicle/dashboard')}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  {loading ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></span>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Submit Request
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BookVehicle;
